import { Box, Button, Divider, Stack, Typography, styled } from '@mui/material';
import { InfoCircle, SearchCircle } from '~/assets/icons';
import { theme } from '~/shared/styles/theme';

export interface Props {
  totalCohortSize: string;
  filtersApplied: number;
  onClear: () => void;
}

const CohortOverviewCard = ({
  totalCohortSize,
  filtersApplied,
  onClear,
}: Props) => {
  return (
    <Card>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <IconCircle>
            <SearchCircle width={24} height={24} />
          </IconCircle>
          <Stack spacing={0.5}>
            <LabelText>TOTAL COHORT SIZE</LabelText>
            <CountText>
              {totalCohortSize} <PatientsSuffix>patients</PatientsSuffix>
            </CountText>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ alignSelf: 'center' }}
        >
          <FiltersAppliedText>
            {filtersApplied} filter{filtersApplied !== 1 ? 's' : ''} applied
          </FiltersAppliedText>
          <ClearAllButton variant="text" onClick={onClear}>
            Clear All
          </ClearAllButton>
        </Stack>
      </Stack>

      <CardDivider />

      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <InfoIconWrapper>
          <InfoCircle width={20} height={20} />
        </InfoIconWrapper>
        <PrivacyText>
          <BoldText>Privacy protection:</BoldText> All counts are shown as
          ranges to prevent re-identification of individual patients. This
          approach meets HIPAA safe harbor guidelines while still providing
          useful aggregate data for clinical trial planning.{' '}
          <BoldText>Multi-valued field:</BoldText> Some fields allow patients to
          have multiple values. For these tables, category totals may exceed the
          cohort total since a single patient can appear in multiple categories.
        </PrivacyText>
      </Stack>
    </Card>
  );
};

export default CohortOverviewCard;

const Card = styled(Box)(() => ({
  width: '100%',
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.custom.cohortCardBorder}`,
  borderRadius: 10,
  padding: theme.spacing(3),
}));

const IconCircle = styled(Stack)(() => ({
  width: 54,
  height: 54,
  borderRadius: '50%',
  backgroundColor: theme.palette.custom.cohortIconBg,
  color: theme.palette.custom.grayGreen,
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

const LabelText = styled(Typography)(() => ({
  fontSize: 10,
  fontWeight: 400,
  color: theme.palette.custom.cohortColumnHeader,
}));

const CountText = styled(Typography)(() => ({
  fontSize: 33,
  fontWeight: 700,
  color: theme.palette.custom.darkBrown,
  lineHeight: 1.2,
}));

const PatientsSuffix = styled('span')({
  fontSize: 16,
  fontWeight: 400,
  verticalAlign: 'middle',
});

const FiltersAppliedText = styled(Typography)(() => ({
  fontSize: 12,
  color: theme.palette.custom.darkBrown,
  paddingRight: theme.spacing(4),
}));

const ClearAllButton = styled(Button)(() => ({
  fontSize: 12,
  fontWeight: 700,
  color: theme.palette.custom.darkBrown,
  textTransform: 'none',
  minWidth: 0,
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
}));

const CardDivider = styled(Divider)(() => ({
  borderColor: theme.palette.custom.cohortCardBorder,
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const InfoIconWrapper = styled(Box)(() => ({
  color: theme.palette.custom.cohortMetaText,
  flexShrink: 0,
  marginTop: 4,
}));

const PrivacyText = styled(Typography)(() => ({
  fontSize: 14,
  color: theme.palette.custom.cohortMetaText,
  lineHeight: 1.5,
}));

const BoldText = styled('span')({ fontWeight: 700 });
